import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../../lib/auth';
import { useT } from '../../i18n';
import { useOs2, useRangeLabel, PageHead, Card, Stat, StageBadge, Dot, Th, Td, money, fmtDateTime, InfoTip, Fade, LoadingState, STAGE_GRADIENTS, Pager } from './os2ui';
import { HealthChip, useHealthScores, ContactTimeline, CampaignIntelligence } from './SalesSmart';

const MINI_LABELS = {
  visitors: ['Visitors', 'Visitantes'], scans: ['Scans', 'Scans'], reports: ['Reports', 'Reportes'],
  leads: ['Leads', 'Leads'], qualified: ['Qualified', 'Calificados'], calls: ['Calls', 'Llamadas'],
  proposals: ['Proposals', 'Propuestas'], customers: ['Won', 'Ganados'],
};

function MiniPipeline({ isEs }) {
  const { data: p } = useOs2('/pipeline');
  const navigate = useNavigate();
  if (!p) return null;
  return (
    <Fade delay={0.05}>
      <Card className="mb-5 px-4 py-4 overflow-x-auto" data-testid="sales-mini-pipeline">
        <div className="flex items-center gap-1.5 min-w-max">
          {p.stages.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <span className="text-gray-300 px-0.5">→</span>}
              <button onClick={() => navigate('/admin/ai-os/pipeline')}
                className="rounded-xl px-3 py-2 text-center text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                style={{ background: STAGE_GRADIENTS[i] || STAGE_GRADIENTS[0] }}
                data-testid={`sales-pipe-${s.key}`}>
                <p className="text-lg font-black tabular-nums leading-none">{s.count}</p>
                <p className="text-[9.5px] uppercase tracking-wider text-white/80 font-bold mt-0.5">{MINI_LABELS[s.key][isEs ? 1 : 0]}</p>
              </button>
            </React.Fragment>
          ))}
          <span className="text-gray-300 px-0.5">→</span>
          <div className="rounded-xl bg-[#F77F11] px-3.5 py-2 text-center shadow-[0_0_16px_rgba(247,127,17,0.35)]">
            <p className="text-lg font-black tabular-nums leading-none text-white">{money(p.mrr)}</p>
            <p className="text-[9.5px] uppercase tracking-wider text-white/80 font-bold mt-0.5">MRR</p>
          </div>
        </div>
      </Card>
    </Fade>
  );
}

/* Sales Intelligence — the CRM. Business question: "Where is the revenue coming from, and what should we close next?" */

const STAGE_OPTS = [
  ['NEW_LEAD', 'New lead', 'Lead nuevo'], ['ENGAGED', 'Engaged', 'Interesado'],
  ['QUALIFIED', 'Qualified', 'Calificado'], ['CALL_BOOKED', 'Call booked', 'Llamada agendada'],
  ['CUSTOMER', 'Customer', 'Cliente'],
];
const AGENTS = ['Eva', 'Stan', 'Rachel', 'Penny', 'Sonny', 'Linda'];

function LeadActions({ p, isEs, onUpdated }) {
  const [stage, setStage] = useState(p.stage);
  const [agent, setAgent] = useState(p.agent || 'Eva');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const body = {};
    if (stage !== p.stage) body.stage = stage;
    if (agent !== (p.agent || 'Eva')) body.agent = agent;
    if (note.trim()) body.note = note.trim();
    if (!Object.keys(body).length) { toast.info(isEs ? 'Nada que guardar' : 'Nothing to save'); return; }
    setSaving(true);
    try {
      const r = await authFetch(`/os2/lead/${encodeURIComponent(p.email)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(isEs ? 'Cambios guardados y auditados ✓' : 'Changes saved & audited ✓');
      setNote('');
      onUpdated();
    } catch (e) {
      toast.error(String(e.message));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50/60 p-4" data-testid="lead-actions">
      <p className="text-[11px] tracking-wider uppercase font-semibold text-gray-500 mb-3">{isEs ? 'Acciones' : 'Actions'}</p>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[11px] text-gray-400">{isEs ? 'Etapa' : 'Stage'}</span>
          <select value={stage} onChange={(e) => setStage(e.target.value)}
            className="mt-0.5 w-full h-9 px-2 rounded-lg border border-gray-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#F77F11]/40"
            data-testid="lead-action-stage">
            {STAGE_OPTS.map(([v, en, es]) => <option key={v} value={v}>{isEs ? es : en}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] text-gray-400">{isEs ? 'Agente responsable' : 'Assigned agent'}</span>
          <select value={agent} onChange={(e) => setAgent(e.target.value)}
            className="mt-0.5 w-full h-9 px-2 rounded-lg border border-gray-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#F77F11]/40"
            data-testid="lead-action-agent">
            {AGENTS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder={isEs ? 'Añadir nota al historial…' : 'Add note to history…'}
        className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#F77F11]/40 resize-none"
        data-testid="lead-action-note" />
      <button onClick={save} disabled={saving}
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-gray-900 hover:bg-[#F77F11] text-white text-[12.5px] font-semibold px-4 py-2 transition-colors disabled:opacity-50"
        data-testid="lead-action-save">
        {saving ? '…' : (isEs ? 'Guardar cambios' : 'Save changes')}
      </button>
    </div>
  );
}

function DetailPanel({ p, isEs, onClose, onDeleted, onUpdated, health }) {
  const [deleting, setDeleting] = useState(false);
  if (!p) return null;
  const removeLead = async () => {
    const msg = isEs
      ? `¿Eliminar definitivamente a ${p.restaurant || p.email}?\n\nSe borrará de leads, CRM y secuencias. Se notificará a las partes (cliente, afiliado y admin) vía el Event Bus.`
      : `Permanently remove ${p.restaurant || p.email}?\n\nIt will be deleted from leads, CRM and sequences. All parties (client, affiliate and admin) will be notified via the Event Bus.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const r = await authFetch(`/os2/lead/${encodeURIComponent(p.email)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(isEs ? 'Registro eliminado — partes notificadas ✓' : 'Record removed — parties notified ✓');
      onDeleted();
    } catch (e) {
      toast.error(String(e.message));
    } finally {
      setDeleting(false);
    }
  };
  const rows = [
    [isEs ? 'Restaurante' : 'Restaurant', p.restaurant], [isEs ? 'Propietario' : 'Owner', p.owner],
    ['Email', p.email], [isEs ? 'Teléfono' : 'Phone', p.phone],
    [isEs ? 'Ciudad' : 'City', p.city], [isEs ? 'País' : 'Country', p.country],
    [isEs ? 'Idioma' : 'Language', p.language], [isEs ? 'Fuente' : 'Source', p.source],
    ['Landing', p.landing_page], ['UTM source', p.utm_source], [isEs ? 'Campaña' : 'Campaign', p.campaign],
    [isEs ? 'Afiliado' : 'Affiliate', p.affiliate_name ? `${p.affiliate_name} (${p.affiliate_code})` : p.affiliate_code],
    ['Index AI Score', p.index_ai_score != null ? `${p.index_ai_score}/100` : null], ['Domain', p.domain],
    ['Lead score', p.lead_score], ['Brevo', p.brevo_status],
    [isEs ? 'Última actividad' : 'Last activity', fmtDateTime(p.last_activity)],
    [isEs ? 'Último evento' : 'Last event', p.last_event],
    [isEs ? 'Ingresos' : 'Revenue', p.revenue ? money(p.revenue) : null],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="sales-detail-panel">
      <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-white border-l border-gray-200 shadow-2xl overflow-y-auto p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-gray-900">{p.restaurant || p.owner || p.email}</h2>
            <p className="text-xs text-gray-400">{p.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="sales-detail-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <StageBadge stage={p.stage} isEs={isEs} />
          <HealthChip score={health?.score} grade={health?.grade} isEs={isEs} />
          <span className="text-[12px] text-gray-500 tabular-nums">{p.probability}% · {p.agent}</span>
        </div>
        <div className="mt-4 rounded-lg bg-[#FFF7ED] border border-orange-100 p-3">
          <p className="text-[11px] tracking-wider uppercase font-semibold text-[#F77F11]">
            {isEs ? 'Próxima acción' : 'Next action'}
            {health?.next_action && <span className="ml-1.5 normal-case rounded-full bg-[#F77F11]/10 px-1.5 py-0.5 text-[9.5px] font-bold text-[#d8651a]">✨ AI</span>}
          </p>
          <p className="text-[13.5px] text-gray-800 mt-1" data-testid="sales-next-action">
            {health?.next_action ? health.next_action[isEs ? 'es' : 'en'] : p.next_action}
          </p>
        </div>
        <div className="mt-5 divide-y divide-gray-100">
          {rows.map(([l, v]) => (
            <div key={l} className="py-2 flex items-start justify-between gap-4">
              <span className="text-[12px] text-gray-400">{l}</span>
              <span className="text-[13px] text-gray-800 text-right break-all">{String(v)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-[12.5px]">
          {[[isEs ? 'Scan completado' : 'Scan completed', p.scan_completed],
            [isEs ? 'Reporte descargado' : 'Report downloaded', p.report_downloaded],
            [isEs ? 'Email enviado' : 'Email sent', p.email_sent],
            [isEs ? 'Email abierto' : 'Email opened', p.email_opened],
            [isEs ? 'Email clicado' : 'Email clicked', p.email_clicked]].map(([l, v]) => (
            <span key={l} className="inline-flex items-center gap-1.5 text-gray-600"><Dot ok={v} /> {l}</span>
          ))}
        </div>
        <ContactTimeline email={p.email} isEs={isEs} />
        <LeadActions p={p} isEs={isEs} onUpdated={onUpdated} />
        <button onClick={removeLead} disabled={deleting}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full border border-red-200 bg-red-50 hover:bg-red-500 hover:border-red-500 hover:text-white text-red-600 text-[13px] font-semibold px-4 py-2.5 transition-colors disabled:opacity-50"
          data-testid="sales-delete-lead">
          <Trash2 className="w-4 h-4" /> {deleting ? '…' : (isEs ? 'Eliminar registro (notifica a las partes)' : 'Remove record (notifies all parties)')}
        </button>
      </div>
    </div>
  );
}

export default function SalesPage({ embedded = false }) {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/sales');
  const { label } = useRangeLabel(isEs);
  const [q, setQ] = useState('');
  const [stage, setStage] = useState('');
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState(null);
  const [params, setParams] = useSearchParams();
  const healthScores = useHealthScores(d?.leads?.map((x) => x.email));

  useEffect(() => {
    const person = params.get('person');
    if (person && d?.leads) {
      const hit = d.leads.find((x) => x.email === person);
      if (hit) setSel(hit);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);

  const rows = useMemo(() => {
    if (!d) return [];
    let r = [...d.leads].sort((a, b) => String(b.last_activity || '').localeCompare(String(a.last_activity || '')));
    if (stage) r = r.filter((x) => x.stage === stage);
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((x) => [x.email, x.restaurant, x.owner, x.company, x.city, x.country, x.affiliate_name, x.affiliate_code]
        .some((v) => (v || '').toLowerCase().includes(s)));
    }
    return r;
  }, [d, q, stage]);
  const PER = 20;
  const pages = Math.max(1, Math.ceil(rows.length / PER));
  const curPage = Math.min(page, pages);
  const pageRows = rows.slice((curPage - 1) * PER, curPage * PER);

  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;

  const f = d.funnel; const c = d.conversion; const t = d.totals;
  const FUNNEL = [
    ['NEW_LEAD', isEs ? 'Leads nuevos' : 'New leads', 'lead'],
    ['QUALIFIED', isEs ? 'Calificados' : 'Qualified', 'stage'],
    ['CALL_BOOKED', isEs ? 'Llamadas' : 'Calls booked', 'meetings'],
    ['CUSTOMER', isEs ? 'Clientes' : 'Customers', 'customers'],
  ];

  return (
    <div data-testid="os2-sales">
      <PageHead
        title={isEs ? 'Inteligencia de Ventas' : 'Sales Intelligence'}
        question={isEs ? '¿De dónde viene el ingreso y qué debemos cerrar ahora?' : 'Where is revenue coming from, and what should we close next?'}
        fresh={embedded ? null : { at: fetchedAt, onReload: reload, loading, isEs }}
        module={embedded ? undefined : 'AI OS'}
      />

      <MiniPipeline isEs={isEs} />

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {FUNNEL.map(([k, l, h]) => <Stat key={k} label={l} value={f[k]} testid={`sales-funnel-${k.toLowerCase()}`} help={h} isEs={isEs} scope="period" />)}
        <Stat label={isEs ? 'Leads nuevos' : 'New leads'} value={t.new_in_period} testid="sales-new-period" help="lead" isEs={isEs} scope="period" />
        <Stat label="Index AI Scans" value={t.scans} testid="sales-scans" help="scan" isEs={isEs} scope="period" />
        <Stat label={isEs ? 'Referidos afiliados' : 'Affiliate referrals'} value={t.affiliate_referrals} testid="sales-referrals" help="referrals" isEs={isEs} scope="period" />
        <Stat label={isEs ? 'Propuestas' : 'Proposals'} value={t.proposals} testid="sales-proposals" isEs={isEs} scope="period" />
      </div>
      <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-400" data-testid="sales-cohort-note">
        {isEs ? `Cohorte filtrada: ${label} · Fuente: Kitxens DB (CRM)` : `Filtered cohort: ${label} · Source: Kitxens DB (CRM)`}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-[12.5px] text-gray-500" data-testid="sales-conversion">
        <span className="inline-flex items-center">{isEs ? 'Tasas de conversión' : 'Conversion rates'}<InfoTip k="conversion" isEs={isEs} /></span>
        <span>{isEs ? 'Lead → Calificado' : 'Lead → Qualified'}: <b className="text-gray-900 tabular-nums">{c.lead_to_qualified}%</b></span>
        <span>{isEs ? 'Calificado → Llamada' : 'Qualified → Call'}: <b className="text-gray-900 tabular-nums">{c.qualified_to_call}%</b></span>
        <span>{isEs ? 'Llamada → Cliente' : 'Call → Customer'}: <b className="text-gray-900 tabular-nums">{c.call_to_customer}%</b></span>
      </div>

      {/* CRM table */}
      <Card className="mt-6 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={isEs ? 'Buscar restaurante, email, ciudad…' : 'Search restaurant, email, city…'}
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#F77F11]/40"
              data-testid="sales-search" />
          </div>
          <select value={stage} onChange={(e) => setStage(e.target.value)}
            className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-[13px] text-gray-600 focus:outline-none"
            data-testid="sales-stage-filter">
            <option value="">{isEs ? 'Todas las etapas' : 'All stages'}</option>
            {['NEW_LEAD', 'QUALIFIED', 'CALL_BOOKED', 'CUSTOMER'].map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <span className="ml-auto text-[12px] text-gray-400 tabular-nums">{rows.length} {isEs ? 'registros' : 'records'} · {isEs ? 'pág.' : 'page'} {curPage}/{pages}</span>
        </div>
        {/* Mobile: leads as vertical cards */}
        <div className="md:hidden divide-y divide-gray-100" data-testid="sales-leads-cards">
          {pageRows.map((p, ri) => (
            <button key={`m-${p.email}-${ri}`} onClick={() => setSel(p)}
              className="w-full text-left px-4 py-3 active:bg-orange-50/60 transition-colors"
              data-testid={`sales-card-${p.email}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-gray-900 text-[14px] truncate">{p.restaurant || p.company || '—'}</p>
                <StageBadge stage={p.stage} isEs={isEs} />
              </div>
              <p className="text-[11.5px] text-gray-400 truncate">{p.owner || p.email}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <HealthChip score={healthScores[p.email]?.score} grade={healthScores[p.email]?.grade} isEs={isEs} />
                {p.city && <span className="text-[11px] text-gray-500">{p.city}</span>}
                {p.revenue ? <span className="text-[11px] font-bold text-gray-900 tabular-nums">{money(p.revenue)}</span> : null}
                <span className="ml-auto text-[10.5px] text-gray-400">{fmtDateTime(p.last_activity)}</span>
              </div>
              {(healthScores[p.email]?.next_action?.[isEs ? 'es' : 'en'] || p.next_action) && (
                <p className="mt-1.5 text-[11.5px] text-[#b45309] leading-snug">✨ {healthScores[p.email]?.next_action?.[isEs ? 'es' : 'en'] || p.next_action}</p>
              )}
            </button>
          ))}
          {!rows.length && <p className="py-8 text-center text-[12px] text-gray-400" data-testid="sales-empty-mobile">{isEs ? 'Sin resultados' : 'No results'}</p>}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse" data-testid="sales-leads-table">
            <thead>
              <tr>
                <Th className="sticky left-0 z-10">{isEs ? 'Restaurante / Propietario' : 'Restaurant / Owner'}</Th>
                <Th help="stage" isEs={isEs}>{isEs ? 'Etapa' : 'Stage'}</Th>
                <Th>{isEs ? 'Salud' : 'Health'}</Th>
                <Th>Email</Th>
                <Th>{isEs ? 'Teléfono' : 'Phone'}</Th>
                <Th>{isEs ? 'Ciudad' : 'City'}</Th>
                <Th>{isEs ? 'País' : 'Country'}</Th>
                <Th help="source" isEs={isEs}>{isEs ? 'Fuente' : 'Source'}</Th>
                <Th help="affiliate" isEs={isEs}>{isEs ? 'Afiliado' : 'Affiliate'}</Th>
                <Th help="campaign" isEs={isEs}>{isEs ? 'Campaña' : 'Campaign'}</Th>
                <Th help="index_ai_score" isEs={isEs}>Index AI</Th>
                <Th help="scan" isEs={isEs}>Scan</Th>
                <Th help="report" isEs={isEs}>{isEs ? 'Reporte' : 'Report'}</Th>
                <Th help="email_sent" isEs={isEs}>{isEs ? 'Email env.' : 'Email sent'}</Th>
                <Th help="email_opened" isEs={isEs}>{isEs ? 'Abierto' : 'Opened'}</Th>
                <Th help="email_clicked" isEs={isEs}>Click</Th>
                <Th help="agent" isEs={isEs}>{isEs ? 'Agente' : 'Agent'}</Th>
                <Th help="last_activity" isEs={isEs}>{isEs ? 'Últ. actividad' : 'Last activity'}</Th>
                <Th help="next_action" isEs={isEs}>{isEs ? 'Próxima acción' : 'Next action'}</Th>
                <Th help="probability" isEs={isEs}>Prob.</Th>
                <Th help="revenue" isEs={isEs}>{isEs ? 'Ingresos' : 'Revenue'}</Th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p, ri) => (
                <tr key={`${p.email}-${ri}`} onClick={() => setSel(p)}
                  className="hover:bg-orange-50/40 cursor-pointer transition-colors"
                  data-testid={`sales-row-${p.email}`}>
                  <Td className="sticky left-0 bg-white z-10 max-w-[220px]">
                    <p className="font-semibold text-gray-900 truncate">{p.restaurant || p.company || '—'}</p>
                    <p className="text-[11.5px] text-gray-400 truncate">{p.owner || p.email}</p>
                  </Td>
                  <Td><StageBadge stage={p.stage} isEs={isEs} /></Td>
                  <Td><HealthChip score={healthScores[p.email]?.score} grade={healthScores[p.email]?.grade} isEs={isEs} /></Td>
                  <Td className="max-w-[180px] truncate">{p.email}</Td>
                  <Td>{p.phone || '—'}</Td>
                  <Td>{p.city || '—'}</Td>
                  <Td>{p.country || '—'}</Td>
                  <Td><span className="text-[11.5px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{p.source || '—'}</span></Td>
                  <Td>{p.affiliate_name || p.affiliate_code || '—'}</Td>
                  <Td>{p.campaign || '—'}</Td>
                  <Td className="tabular-nums font-semibold">{p.index_ai_score != null ? p.index_ai_score : '—'}</Td>
                  <Td><Dot ok={p.scan_completed} /></Td>
                  <Td><Dot ok={p.report_downloaded} /></Td>
                  <Td><Dot ok={p.email_sent} /></Td>
                  <Td><Dot ok={p.email_opened} /></Td>
                  <Td><Dot ok={p.email_clicked} /></Td>
                  <Td>{p.agent}</Td>
                  <Td className="text-gray-400">{fmtDateTime(p.last_activity)}</Td>
                  <Td className="max-w-[190px] truncate text-[#b45309]">{healthScores[p.email]?.next_action?.[isEs ? 'es' : 'en'] || p.next_action}</Td>
                  <Td className="tabular-nums">{p.probability}%</Td>
                  <Td className="tabular-nums font-semibold">{p.revenue ? money(p.revenue) : '—'}</Td>
                </tr>
              ))}
              {!rows.length && (
                <tr><Td className="text-center text-gray-400 py-8" colSpan={21} data-testid="sales-empty">{isEs ? 'Sin resultados' : 'No results'}</Td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pb-3"><Pager page={curPage} pages={pages} setPage={setPage} testid="sales-pager" /></div>
      </Card>

      <CampaignIntelligence isEs={isEs} />

      <DetailPanel p={sel} isEs={isEs} onClose={() => setSel(null)} onDeleted={() => { setSel(null); reload(); }} onUpdated={reload} health={sel ? healthScores[sel.email] : null} />
    </div>
  );
}
